import { format } from 'date-fns';
import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as AddIcon } from '@assets/svg/add.svg';

import StatusTypeItem from '@components/common/StatusTypeItem/StatusTypeItem';
import Button from '@components/uikit/Button/Button';

import { MODAL_NAMES } from '@constants/modalRenderers';

import useTranslation from '@hooks/useTranslation';

import type { IEquipmentDetails } from '@model/equipmentDetails';

import { useAvailableEquipmentTypesQuery } from '@services/useAvailableEquipmentTypesQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';
import { useModalActions } from '@store/useModalStore';

import { getPointInfo } from '../../helpers/getPointInfo';
import type { IAiHistory } from '../../model/aiHistory';
import EquipmentDetailsAIHistorySkeleton from './EquipmentDetailsAIHistorySkeleton';
import style from './style.module.scss';

interface IProps {
  historyList?: Array<IAiHistory>;
  detailsData?: IEquipmentDetails;
  currentEquipmentType?: string;
  isEquipmentLoading?: boolean;
}

const AVAILABLE_AI_COUNT = 3;

const EquipmentDetailsAIHistory: React.FC<IProps> = ({
  historyList = [],
  detailsData,
  currentEquipmentType,
  isEquipmentLoading = false,
}) => {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { setActiveModal } = useModalActions();

  const locationSelectValue = selectAppLocationSelectValue();

  const { data: profileData } = useUserProfileQuery();

  const { data: equipmentTypesListData } = useAvailableEquipmentTypesQuery({
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });

  const dateFormat = profileData?.dateFormat.date || '';

  const openedActionItem = historyList.find((item) => !item.closed_at);

  const lastActionItem = openedActionItem || historyList?.[0];

  const tableHistoryList = historyList.slice(0, AVAILABLE_AI_COUNT);

  const getActionText = (item: IAiHistory) => {
    if (+item.state === 5) {
      return t('No action is required.');
    }

    return item.cause || item.text || '';
  };

  const getDates = (actionItem: IAiHistory) => {
    let currentDate = format(actionItem.time_created, dateFormat);

    if (actionItem.closed_at) {
      currentDate += ` - ${format(actionItem.closed_at, dateFormat)}`;
    }

    return currentDate;
  };

  const pointInfo = getPointInfo(detailsData, currentEquipmentType);

  const showActionsItemAddButton = !openedActionItem && !!profileData?.userAccess?.manageData && !!pointInfo;

  return (
    <div className={style.historyBlock}>
      <div className={style.blockTitle}>{t('Action item history')}</div>
      <div className={style.blockContainer}>
        {!tableHistoryList.length ? (
          <div className={style.emptyDataBlock}>
            <span>{t('No action items were found')}</span>
          </div>
        ) : (
          <table className={style.historyAiTable} cellSpacing="0" cellPadding="0">
            <thead>
              <tr>
                <th>{t('Name/date')}</th>
                <th>{t('Action')}</th>
                <th className={style.statusTh}>{t('Status')}</th>
              </tr>
            </thead>
            <tbody>
              {isEquipmentLoading && <EquipmentDetailsAIHistorySkeleton />}
              {!isEquipmentLoading &&
                tableHistoryList.map((ai) => (
                  <tr
                    key={uuidv4()}
                    onClick={() =>
                      navigate(`/customer/${customerId}/ai/${ai.id}`, { state: { prevPage: pathname } })
                    }
                  >
                    <td>
                      <div className={style.infoTitle}>{ai.title}</div>
                      <p className={style.infoDate}>{getDates(ai)}</p>
                    </td>
                    <td>
                      <p className={style.infoText} dangerouslySetInnerHTML={{ __html: getActionText(ai) }} />
                    </td>
                    <td className={style.alignRight}>
                      <StatusTypeItem item={ai} onlyTitle={true} hideTroubleshooting={true} />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
        <div className={style.buttonsContainer}>
          {showActionsItemAddButton && (
            <Button
              type="secondary"
              text={t('Add Action Item')}
              onClick={() => {
                const equipmentTypeItem = equipmentTypesListData?.find(
                  (item) => item.id === currentEquipmentType,
                );

                setActiveModal({
                  name: MODAL_NAMES.addActionItemModal,
                  options: {
                    filledData: {
                      installationPoint: pointInfo,
                      aiType: 'waitesWireless',
                      equipmentType: equipmentTypeItem,
                    },
                  },
                });
              }}
              leftElement={<AddIcon className={style.addIcon} />}
            />
          )}
          {historyList.length > AVAILABLE_AI_COUNT && (
            <Link
              to={`/customer/${customerId}/ai/${lastActionItem?.id}/history`}
              state={{ prevPage: pathname }}
              className={style.viewHistoryBtn}
            >
              {t('View all history')}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default EquipmentDetailsAIHistory;
